<template>
	<div>
		<modal
			v-show="!isTopupModalVisible"
			size="medium"
			:hide-footer="true"
			@close="$emit('close')"
		>
			<template #body>
				<div>
					<!-- Balance Header Card -->
					<div class="card text-white mb-3 border-0 bg-info">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-center mb-2">
								<small class="text-xs fw-bold">
									Saldo Saat Ini
								</small>
								<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.8;">
									<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"></path>
									<path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"></path>
								</svg>
							</div>
							<h1 class="h2 fw-bold mb-0">
								<b class="">
									{{ formatMoney(currentBalance) }}
								</b>
							</h1>
						</div>
					</div>

					<!-- Product Summary -->
					<div class="card border-0 mb-3">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-center py-2 border-bottom">
								<small class="text-xs fw-bold mb-0">Supplier</small>
								<small class="fw-bold mb-0 text-dark">{{ supplierName }}</small>
							</div>
							<div class="d-flex justify-content-between align-items-center py-2 border-bottom">
								<small class="text-xs fw-bold mb-0">Jumlah Paket</small>
								<small class="fw-bold mb-0 text-dark">{{ totalPackages }} paket</small>
							</div>

							<!-- Cost Breakdown -->
							<div class="mt-3">
								<div class="d-flex justify-content-between mb-3">
									<small class="text-secondary">Total Harga Barang</small>
									<small class="fw-semibold text-dark">{{ formatMoney(subtotalAmount) }}</small>
								</div>
								<div class="d-flex justify-content-between mb-3">
									<small class="text-secondary">Total Handling Fee</small>
									<small class="fw-semibold text-dark">{{ formatMoney(totalHandlingFee) }}</small>
								</div>
								<div class="d-flex justify-content-between mb-3">
									<small class="text-secondary">Biaya Layanan</small>
									<div>
										<small class="fw-semibold text-dark">
											{{ formatMoney(platformFee) }}
										</small>
										<small
											class="text-decoration-line-through text-danger"
											v-if="serviceFee && serviceFee.type === 'fixed' && serviceFee.value && serviceFee.value !== 0 && serviceFee.value !== serviceFee.final_value"
										>
											{{ formatMoney(serviceFee.value) }}
										</small>

									</div>
								</div>

								<!-- Total -->
								<div class="d-flex justify-content-between align-items-center pt-3 mt-3 border-top">
									<div>
										<span class="d-block text-xs text-uppercase fw-bold mb-1">Total Tagihan</span>
									</div>
									<div class="d-flex align-items-center">
										<div class="mr-2">
											<div v-if="isBalanceSufficient" class="sufficient-balance">
												Saldo Cukup
											</div>
											<div v-else class="insufficient-balance">
												Saldo tidak mencukupi
											</div>
										</div>
										<div class="fw-bold text-dark">
											<b>
												{{ formatMoney(totalAmount) }}
											</b>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Action Buttons -->
					<div class="row g-3 mt-4">
						<!-- Close/Cancel Button -->
						<div class="col-sm-2">
							<button
								class="btn btn-default w-100 border py-3"
								style="border: none;"
								:disabled="isSubmitting"
								@click="$emit('close')"
							>
								<i class="material-icons">close</i>
							</button>
						</div>
						<!-- Topup Button (shown when balance insufficient) -->
						<div class="col-sm-10 pl-0" v-if="!isBalanceSufficient">
							<button
								class="btn btn-primary py-3 fw-bold w-100"
								@click="handleTopup"
							>
								Topup
							</button>
						</div>

						<!-- Confirm Button (shown when balance sufficient) -->
						<div class="col-sm-10 pl-0" v-else>
							<button
								class="btn btn-primary py-3 fw-bold w-100"
								:class="{ 'is-loading': isSubmitting }"
								:disabled="isBtnDisabled || isSubmitting"
								@click="submit"
							>
								Konfirmasi
							</button>
						</div>
					</div>
				</div>
			</template>
		</modal>

		<!-- Topup Modal -->
		<topup-modal
			:is-visible="isTopupModalVisible"
			:initial-amount="topupAmount"
			@close="isTopupModalVisible = false"
			@success="handleTopupSuccess"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import Modal from '@/components/Modal.vue';
import TopupModal from '@/components/TopupModal.vue';
import Wallet from '@/api/wallet';
import Media from '@/api/media';
import { payImportOrderWithBalance } from '@/api/private-order';

import {
	formatMoney,
	getSimpleProductOriginalPrice,
} from '@/lib/helper';
import {
	PLATFORM_FEE_PER_ORDER,
	FULFILLMENT_FEE_PER_ORDER,
} from '@/lib/statics';

export default {
	name: 'ProcessImportOrder',
	components: {
		Modal,
		TopupModal,
	},
	props: {
		summaryItems: {
			type: Array,
			default: () => [],
		},
		selectedProducts: {
			type: Array,
			default: () => [],
		},
		supplierId: {
			type: String,
			default: '',
		},
		mappingDataPerPackage: {
			type: Array,
			default: () => [],
		},
		mappingDataPerProduct: {
			type: Array,
			default: () => [],
		},
		shippingLabelFile: {
			type: File,
			default: null,
		},
		excelFile: {
			type: File,
			default: null,
		},
	},
	data() {
		return {
			isSubmitting: false,
			currentBalance: 0,
			isTopupModalVisible: false,
		};
	},
	computed: {
		...mapGetters({
			isAdmin: 'isAdmin',
			isSupplier: 'isSupplier',
			isDropshipper: 'isDropshipper',
			serviceFee: 'importWholesaleOrder/serviceFee',
			selectedSupplier: 'importWholesaleOrder/selectedSupplier',
		}),
		supplierName() {
			return this.selectedSupplier ? this.selectedSupplier.name : '-';
		},
		totalProducts() {
			return this.summaryItems.length;
		},
		totalQuantity() {
			return this.summaryItems.reduce((sum, item) => sum + item.quantity, 0);
		},
		totalPackages() {
			return this.mappingDataPerPackage.length;
		},
		subtotalAmount() {
			// Total harga produk tanpa handling fee
			return this.summaryItems.reduce((sum, item) => sum + item.subtotal, 0);
		},
		totalHandlingFee() {
			// Gunakan getter dari Vuex store
			return this.$store.getters['importWholesaleOrder/totalHandlingFee'];
		},
		platformFee() {
			// Service fee dari API
			let fee = PLATFORM_FEE_PER_ORDER;
			const serviceFeeConfig = this.serviceFee;
			if (serviceFeeConfig && serviceFeeConfig.final_value) {
				fee = serviceFeeConfig.final_value;
			}
			return fee;
		},
		fulfillmentFee() {
			return this.totalPackages * FULFILLMENT_FEE_PER_ORDER;
		},
		totalAmount() {
			// Total = subtotal produk + total handling fee + platform fee
			return this.subtotalAmount + this.totalHandlingFee + this.platformFee;
		},
		isBalanceSufficient() {
			return this.currentBalance >= this.totalAmount;
		},
		topupAmount() {
			// Calculate deficit amount (minimum 100000)
			const deficit = this.totalAmount - this.currentBalance;
			return deficit < 100000 ? 100000 : deficit;
		},
		isBtnDisabled() {
			return !this.shippingLabelFile || !this.excelFile || this.summaryItems.length === 0;
		},
	},
	created() {
		this.fetchBalance();
	},
	methods: {
		formatMoney,
		getSimpleProductOriginalPrice,
		fetchBalance() {
			const callback = (response) => {
				const data = response?.data || response || {};
				this.currentBalance = data.balance || 0;
			};
			const errorCallback = (error) => {
				console.error('Failed to fetch balance:', error);
				this.currentBalance = 0;
			};
			Wallet.getBalance(callback, errorCallback);
		},
		handleTopup() {
			this.isTopupModalVisible = true;
		},
		handleTopupSuccess() {
			// Refresh balance after successful topup
			this.fetchBalance();
		},
		submit() {
			this.isSubmitting = true;

			// Upload files first
			let shippingLabelUrl = null;
			let excelFileUrl = null;
			const uploadErrors = [];

			const uploadShippingLabel = new Promise((resolve, reject) => {
				const uploadParams = new FormData();
				uploadParams.append('file', this.shippingLabelFile);
				uploadParams.append('prefix', 'shipping-labels');
				uploadParams.append('resize', false);

				Media.uploadShippingLabel(
					uploadParams,
					(response) => {
						shippingLabelUrl = response.data;
						resolve(shippingLabelUrl);
					},
					(error) => {
						uploadErrors.push('Gagal mengupload shipping label');
						console.log(error);
						reject(error);
					},
				);
			});

			const uploadExcelFile = new Promise((resolve, reject) => {
				const uploadParams = new FormData();
				uploadParams.append('file', this.excelFile);
				uploadParams.append('prefix', 'import-orders');
				uploadParams.append('resize', false);

				Media.uploadFile(
					uploadParams,
					(response) => {
						excelFileUrl = response.data;
						resolve(excelFileUrl);
					},
					(error) => {
						uploadErrors.push('Gagal mengupload file excel');
						reject(error);
					},
				);
			});

			// Wait for both uploads to complete
			Promise.all([uploadShippingLabel, uploadExcelFile])
				.then(() => {
					// Both files uploaded successfully, now prepare the payload
					const mappingPerProducts = this.mappingDataPerProduct
						.filter(item => item.isValid && item.selectedProductIds && item.selectedProductIds.length > 0)
						.map(item => ({
							product_name: item.productName,
							quantity: item.quantity,
							selected_product_ids: item.selectedProductIds,
						}));

					const mappingPerPackage = this.mappingDataPerPackage.map(pkg => ({
						order_id: pkg.orderId,
						items: pkg.items.map(item => ({
							product_name: item.productName,
							quantity: item.quantity,
						})),
					}));

					// Prepare selected products for backend (grouped by product ID)
					const selectedProducts = this.selectedProducts.map((product) => {
						const basePrice = getSimpleProductOriginalPrice(product);
						return ({
							product_id: product._id,
							product_name: product.name,
							product_sku: product.code,
							price: basePrice,
							quantity: product.quantity,
							subtotal: basePrice * product.quantity,
						});
					});

					// Prepare JSON payload with snake_case
					const payload = {
						supplier_id: this.supplierId,
						mapping_per_products: mappingPerProducts,
						mapping_per_package: mappingPerPackage,
						selected_products: selectedProducts,
						shipping_label_file_url: `${this.mediaUrlPrefix}/${shippingLabelUrl}`,
						order_file_url: `${this.mediaUrlPrefix}/${excelFileUrl}`,
					};

					// Call API with JSON payload
					payImportOrderWithBalance(
						payload,
						() => {
							this.isSubmitting = false;
							this.$notify({
								group: 'app',
								title: 'Success',
								text: 'Import order berhasil diproses',
								type: 'success',
							});
							this.$emit('close');
							this.$router.push({ name: 'MonitoringPrivateOrder' });
						},
						(error) => {
							console.error('Error submitting import order:', error);
							this.$notify({
								group: 'app',
								title: 'Error',
								text: error.response?.data?.message || 'Gagal memproses import order',
								type: 'error',
							});
							this.isSubmitting = false;
						},
					);
				})
				.catch((error) => {
					console.error('Upload error:', error);
					this.$notify({
						group: 'app',
						title: 'Error',
						text: uploadErrors.join('. '),
						type: 'error',
					});
					this.isSubmitting = false;
				});
		},
	},
};
</script>
<style scoped>
.fee-discounted {
    text-decoration: line-through;
}

.sufficient-balance {
	background-color: #d4edda;
	color: #155724;
	padding: 5px 10px;
	border-radius: 5px;
	font-weight: bold;
	font-size: 11px;
}

.insufficient-balance {
	background-color: #ff3647;
	color: #fff;
	padding: 5px 10px;
	border-radius: 5px;
	font-weight: bold;
	font-size: 11px;
}

.text-decoration-line-through  {
	text-decoration: line-through;
}
</style>
